import React, { useEffect, useRef, useState } from 'react';
import { ExpandableMenuProps } from 'src/app/molecules/Header/Header/HeaderLinks';
import {
  MegaMenuChildContainer,
  MegaMenuContainer,
  MegaMenuExitZone,
  MegaMenuWrapper,
} from './MegaMenuContainer.styles';
import { SpacingPropKeys } from 'src/app/styles/spacing';

interface MegaMenuProps extends ExpandableMenuProps {
  children: React.ReactNode;
  padding?: SpacingPropKeys;
}

const MegaMenu = ({ isOpen, onClose, children, padding = 'spacing-lg' }: MegaMenuProps): JSX.Element => {
  const [fallbackRenderHeight, setFallbackRenderHeight] = useState(0);
  const menuContainerRef = useRef<HTMLDivElement>(null);
  const hasMounted = useRef(false);

  useEffect(() => {
    if (!menuContainerRef.current) {
      return;
    }
    // React Portal executes component & props before REF is set, a state fallback height upon render is needed to avoid height 0 glitches
    setFallbackRenderHeight(menuContainerRef.current.getBoundingClientRect().height);
  }, [menuContainerRef.current, isOpen]);

  // Height gets re-assigned on every isOpen change.
  const maxHeight = menuContainerRef.current?.getBoundingClientRect().height || fallbackRenderHeight;

  useEffect(() => {
    if (isOpen) {
      hasMounted.current = true;
    }
  }, [isOpen]);

  // hasMounted has the purpose to avoid rendering the element before the first mount,
  // which will avoid react minified errors
  if (!hasMounted.current && !isOpen) {
    return null;
  }

  return (
    <MegaMenuWrapper>
      <MegaMenuContainer
        $isOpen={isOpen}
        $maxHeight={maxHeight}
      >
        <MegaMenuChildContainer
          ref={menuContainerRef}
          $padding={padding}
        >
          {children}
        </MegaMenuChildContainer>
      </MegaMenuContainer>
      {isOpen && <MegaMenuExitZone onClick={onClose} />}
    </MegaMenuWrapper>
  );
};

export default MegaMenu;
